import { createSlice } from "@reduxjs/toolkit"

const initialState = {
  items: [],
  itemsFilter: "ALL",
  status: "idle",
  totalMessagesCount: undefined,
  totalMessagesStatusCounts: undefined,
  lastFetchTime: null, // Add a timestamp field
}

export const totalMessagesCount = items => {
  if (!items?.length) return
  const count =
    items?.reduce(
      (sum, val) => (sum = val.status === "NEW" ? sum + 1 : sum),
      0
    ) || 0
  const newMsgs = items?.reduce((sum, item) => {
    sum = sum + (item.chatMessages > 0 ? 1 : 0)
    return sum
  }, 0)
  return count + newMsgs
}

const statusCounts = items =>
  items.reduce(
    (acc, booking) => {
      acc[booking.status] = (acc[booking.status] || 0) + 1
      acc.ALL += 1
      return acc
    },
    { ALL: 0 }
  )

// const sortBookings = items => {
//   const messagesSort = (a, b) => {
//     if (a.chatNewMessages > b.chatNewMessages) {
//       return -1
//     } else {
//       return 1
//     }
//   }
//   const sorted = [...items]
//     .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
//     .sort(messagesSort)
//   debugger
//   return sorted
// }

const sortBookings = bookings => {
  const hasNewMessages = item => item?.chatNewMessages > 0
  const hasStatus = status => item => item.status === status
  const hasValidUsers = item => item?.hostUser && item?.guestUser
  
  // Filter out expired unpaid bookings
  const isNotExpiredUnpaid = item => {
    // If booking has been paid/confirmed (BOOKED, CURRENT, COMPLETED), always show
    if (["BOOKED", "CURRENT", "COMPLETED"].includes(item.status)) {
      return true
    }
    
    // If booking is still pending payment (PAY status), show it regardless of date
    if (item.status === "PAY") {
      return true
    }
    
    // For unpaid bookings (NEW, REQUEST), check if departure date has passed
    const departureDate = item.departurePrimary || item.departureHost
    if (!departureDate) {
      return true // Keep bookings without departure date
    }
    
    const now = new Date()
    const departure = new Date(departureDate)
    
    // Only show if departure date is in the future
    return departure > now
  }

  const filteredBookings = bookings
    .filter(hasValidUsers)
    .filter(isNotExpiredUnpaid)

  const newMessageBookings = filteredBookings
    .filter(hasNewMessages)
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))

  const payBookings = filteredBookings
    .filter(item => !hasNewMessages(item) && hasStatus("PAY")(item))
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))

  const newBookings = filteredBookings
    .filter(item => !hasNewMessages(item) && hasStatus("NEW")(item))
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))

  const declinedBookings = filteredBookings
    .filter(item => !hasNewMessages(item) && hasStatus("DECLINED")(item))
    //add filter to remove declined over 10 days old by updatedAt
    .filter(item => {
      const updatedAt = new Date(item.updatedAt)
      const tenDaysAgo = new Date()
      tenDaysAgo.setDate(tenDaysAgo.getDate() - 10)
      return updatedAt >= tenDaysAgo
    })
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))

  const otherBookings = filteredBookings
    .filter(
      item =>
        !hasNewMessages(item) &&
        !["PAY", "NEW", "DECLINED"].includes(item.status)
    )
    //add a filter to exclude items with updatedAt older than 365 days
    .filter(item => {
      const updatedAt = new Date(item.updatedAt)
      const oneYearAgo = new Date()
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
      return updatedAt >= oneYearAgo
    })
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))

  return [
    ...newMessageBookings,
    ...payBookings,
    ...newBookings,
    ...otherBookings,
    ...declinedBookings,
  ]
}

const InboxSlice = createSlice({
  name: "Inbox",
  initialState,
  reducers: {
    InboxLoading(state, action) {
      state.status = "loading"
    },
    InboxAddItems(state, action) {
      const sortedFiltered = sortBookings(action.payload)
      state.items = sortedFiltered
      state.totalMessagesCount = totalMessagesCount(sortedFiltered)
      state.totalMessagesStatusCounts = statusCounts(sortedFiltered)
      state.status = "idle"
      state.lastFetchTime = Date.now() // Set the last fetch time
    },
    InboxSetItemFilter(state, action) {
      state.itemsFilter = action.payload.itemsFilter
    },
  },
  extraReducers: {},
})

export const {
  InboxAddItems,
  InboxSetItemFilter,
  InboxLoading,
} = InboxSlice.actions

export default InboxSlice.reducer
